import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Logo from "./Logo";

const WHATSAPP_URL = "https://wa.me/918796564619";

const EXPLORE_LINKS = [
  { label: "How It Works", type: "anchor", target: "how-it-works" },
  { label: "Collection", type: "route", href: "/collection" },
  { label: "Why Aurevia", type: "anchor", target: "why" },
  { label: "Meet the Team", type: "anchor", target: "contact" },
];

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleExploreClick = (link) => {
    if (link.type === "route") {
      navigate(link.href);
      return;
    }
    if (location.pathname === "/") {
      scrollToId(link.target);
    } else {
      navigate("/");
      setTimeout(() => scrollToId(link.target), 60);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-brand-charcoal border-t border-white/10 mt-16 md:mt-24"
    >
      {/* subtle gold hairline at top */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(201,169,97,0.25), transparent)",
        }}
      />

      <div className="editorial-container pt-12 md:pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-10">
          {/* Brand column */}
          <div className="md:col-span-5">
            <Logo size="lg" />
            <p className="mt-8 font-serif italic text-2xl text-brand-gold/90 leading-snug max-w-xs">
              The quarry, at your doorstep.
            </p>
            <p className="mt-6 font-sans text-sm text-brand-cream/60 leading-relaxed max-w-sm">
              India&apos;s premium marble execution partner. Direct from
              Rajasthan to your home - no showroom, no middlemen.
            </p>
          </div>

          {/* Contact column */}
          <div className="md:col-span-4">
            <h4
              data-testid="footer-contact-heading"
              className="font-sans text-[10px] uppercase tracking-[0.3em] text-brand-cream/40 mb-6"
            >
              Contact
            </h4>
            <ul className="space-y-4 font-sans text-sm text-brand-cream/80">
              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="hover:text-brand-gold transition-colors inline-flex items-center gap-2"
                >
                  <span className="text-brand-cream/40 text-xs">WhatsApp</span>
                  <span>+91 87965 64619</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:kshitij.gupta@aureviastones.com"
                  data-testid="footer-email-link"
                  className="hover:text-brand-gold transition-colors inline-flex items-center gap-2"
                >
                  <span className="text-brand-cream/40 text-xs">Email</span>
                  <span>kshitij.gupta@aureviastones.com</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <h4
              data-testid="footer-links-heading"
              className="font-sans text-[10px] uppercase tracking-[0.3em] text-brand-cream/40 mb-6"
            >
              Explore
            </h4>
            <ul className="space-y-4 font-sans text-sm text-brand-cream/80">
              {EXPLORE_LINKS.map((link) => (
                <li key={link.label}>
                  <button
                    type="button"
                    onClick={() => handleExploreClick(link)}
                    data-testid={`footer-link-${link.label
                      .toLowerCase()
                      .replace(/\s+/g, "-")}`}
                    className="hover:text-brand-gold transition-colors text-left"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="mt-12 md:mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-3 font-sans text-[11px] tracking-wide text-brand-cream/40">
          <p data-testid="footer-copyright">
            © 2026 Aurevia Stones Pvt. Ltd. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={() => navigate("/privacypolicy")}
              data-testid="footer-privacy-policy-link"
              className="hover:text-brand-gold transition-colors"
            >
              Privacy Policy
            </button>
            <p className="font-serif italic text-brand-cream/60">
              Crafted with care in India.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
